// /components — M3.3 demo route showcasing every shared primitive.
// -----------------------------------------------------------------
// Per docs/architecture/dispatches/m3-3-shared-lib.md § 6.11 + § 6.12
// + § 4.3.
//
// Feature-flagged behind `VITE_FF_COMPONENTS_DEMO`:
//   .env.staging    → on  (route renders normally)
//   .env.production → unset (the route renders an Alert explaining
//                            the flag is off; reaches via direct URL
//                            only — no sidebar nav exposes it)
//
// Surface: every primitive in every state, organized into Mantine
// `<Card>` sections by family. M3.3-P2 lead visual review walks the
// page top-to-bottom and confirms every state renders cleanly.

import {
  Alert,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { ApproveButton } from '@/components/ApproveButton';
import { AudioPill } from '@/components/AudioPill';
import { BulkActionMenu } from '@/components/BulkActionMenu';
import { DetailedStatusBadges } from '@/components/DetailedStatusBadges';
import { StatusBadge } from '@/components/StatusBadge';
import { useOptimisticMutation } from '@/hooks/useOptimisticMutation';
import { isFeatureEnabled } from '@/lib/featureFlags';

const FF_ON = isFeatureEnabled('COMPONENTS_DEMO');

// ---------------------------------------------------------------------------
// Mock data fixtures — one per status code + per detailed state.
// ---------------------------------------------------------------------------

type TrackFixture = {
  label: string;
  data: Record<string, unknown>;
};

const STATUS_CODE_FIXTURES: ReadonlyArray<TrackFixture> = [
  { label: 'Code 0 — Empty', data: {} },
  {
    label: 'Code 1 — Initial Crawl',
    data: { localized: { fr: { title: 'La Joconde' } } },
  },
  {
    label: 'Code 2 — Awaiting Research',
    data: {
      processing_status: 'awaiting_research',
      localized: { fr: { title: 'x' } },
    },
  },
  {
    label: 'Code 3 — FR Audio Pending',
    data: {
      localized: { fr: { audioguide_script: '...' } },
      language_status: { fr: { script_is_approved: true } },
    },
  },
  {
    label: 'Code 4 — FR Audio Pending (EN script approved)',
    data: {
      localized: {
        fr: { audioguide_script: 'x' },
        en: { audioguide_script: 'y' },
      },
      language_status: {
        fr: { script_is_approved: true },
        en: { script_is_approved: true },
      },
    },
  },
  {
    label: 'Code 5 — FR Audio Approved',
    data: {
      localized: { fr: { trackUrl: 'https://audio/fr.mp3' } },
      language_status: { fr: { audio_is_approved: true } },
    },
  },
  {
    label: 'Code 6 — EN Audio Approved (terminal)',
    data: {
      localized: {
        fr: { trackUrl: 'https://audio/fr.mp3' },
        en: { trackUrl: 'https://audio/en.mp3' },
      },
      language_status: {
        fr: { audio_is_approved: true },
        en: { audio_is_approved: true },
      },
    },
  },
];

const DETAILED_FIXTURES: ReadonlyArray<TrackFixture> = [
  { label: 'Empty', data: {} },
  {
    label: 'Audio Approved (no gender suffix)',
    data: {
      localized: { fr: { trackUrl: 'https://audio/x.mp3' } },
      language_status: { fr: { audio_is_approved: true } },
    },
  },
  {
    label: 'Audio Approved + voice-gender suffix',
    data: {
      localized: {
        fr: { trackUrl: 'https://audio/x.mp3', voice_id_used: 'Aoede_fr' },
      },
      language_status: { fr: { audio_is_approved: true } },
    },
  },
  {
    label: 'Audio Generated',
    data: {
      localized: { fr: { trackUrl: 'https://audio/x.mp3' } },
      language_status: { fr: { audio_is_approved: false } },
    },
  },
  // M-Pipeline.6c (ADJ-2): the "Tour Reviewed" (purple #6f42c1) demo
  // fixture was removed — Tour Review was decommissioned in `.3` and the
  // `tour_reviewed`-driven label no longer exists.
  {
    label: 'Audio Pending',
    data: {
      localized: { fr: { audioguide_script: '...', tts_text: 'ssml' } },
      language_status: { fr: { script_is_approved: true } },
    },
  },
  {
    label: 'TTS Format Pending',
    data: {
      localized: { fr: { audioguide_script: '...' } },
      language_status: { fr: { script_is_approved: true } },
    },
  },
  {
    label: 'Script Pending',
    data: {
      localized: { fr: { audioguide_script: '...' } },
      language_status: { fr: { script_is_approved: false } },
    },
  },
  {
    label: 'EN Translation Paused',
    data: { language_status: { en: { translation_paused: true } } },
  },
];

// ---------------------------------------------------------------------------
// Optimistic-mutation demo
// ---------------------------------------------------------------------------

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const OptimisticMutationDemo = () => {
  const successMutation = useOptimisticMutation<void, void>({
    mutationFn: async () => {
      await sleep(600);
    },
    successMessage: 'Mutation succeeded after 600ms',
  });

  const rollbackMutation = useOptimisticMutation<void, void>({
    mutationFn: async () => {
      await sleep(600);
      throw new Error('Simulated failure for rollback demo');
    },
  });

  return (
    <Stack gap='xs'>
      <Group>
        <Button
          onClick={() => successMutation.mutate()}
          loading={successMutation.isPending}
          color='green'
        >
          Test optimistic mutation (success)
        </Button>
        <Button
          onClick={() => rollbackMutation.mutate()}
          loading={rollbackMutation.isPending}
          color='red'
          variant='light'
        >
          Test optimistic mutation (rollback)
        </Button>
        {(successMutation.isPending || rollbackMutation.isPending) && (
          <Loader size='sm' />
        )}
      </Group>
      <Text size='xs' c='dimmed'>
        Both buttons trigger a 600ms delay; success surfaces a green Mantine
        notification, rollback surfaces a red one with the thrown error message.
      </Text>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Approve-button + audio-pill state matrix
// ---------------------------------------------------------------------------

const ApproveButtonRow = () => {
  const [clicks, setClicks] = useState(0);
  return (
    <Stack gap='xs'>
      <Group gap='md'>
        <ApproveButton isApproved={false} hasContent={false} />
        <ApproveButton
          isApproved={false}
          hasContent
          onClick={() => setClicks((c) => c + 1)}
        />
        <ApproveButton isApproved hasContent />
        <ApproveButton
          isApproved
          hasContent
          resubmittable
          onClick={() => setClicks((c) => c + 1)}
        />
      </Group>
      <Text size='xs' c='dimmed'>
        Empty / Approve / Approved (locked) / Approved (resubmittable). Click
        counter: {clicks}.
      </Text>
    </Stack>
  );
};

const AudioPillRow = () => (
  <Group gap='md'>
    <AudioPill audioApproved trackUrl='https://audio/x.mp3' />
    <AudioPill audioApproved={false} trackUrl='https://audio/x.mp3' />
    <AudioPill audioApproved={false} />
  </Group>
);

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

const ComponentsDemoRoute = () => {
  if (!FF_ON) {
    return (
      <Alert color='yellow' variant='light' title='Feature flag off'>
        <Text size='sm'>
          The <code>VITE_FF_COMPONENTS_DEMO</code> feature flag is off in this
          environment, so the demo surface is hidden. Set the flag to{' '}
          <code>on</code> in <code>.env.staging</code> to view it. The flag is
          intentionally absent from <code>.env.production</code> so this route
          is never exposed to end-users in prod.
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap='lg'>
      <Title order={2}>M3.3 component demo</Title>
      <Text size='sm' c='dimmed'>
        Visual review surface for every shared primitive in every state. See
        dispatch m3-3-shared-lib.md § 5.2 (M3.3-P2) for the lead review
        checklist.
      </Text>

      <Card withBorder shadow='xs' padding='md' radius='md'>
        <Stack>
          <Title order={4}>StatusBadge — codes 0..6</Title>
          {STATUS_CODE_FIXTURES.map((f) => (
            <Group key={f.label} gap='md'>
              <Text size='sm' w={320}>
                {f.label}
              </Text>
              <StatusBadge data={f.data} />
            </Group>
          ))}
        </Stack>
      </Card>

      <Card withBorder shadow='xs' padding='md' radius='md'>
        <Stack>
          <Title order={4}>DetailedStatusBadges — every label</Title>
          {DETAILED_FIXTURES.map((f) => (
            <Group key={f.label} gap='md' wrap='nowrap'>
              <Text size='sm' w={360}>
                {f.label}
              </Text>
              {/* Demo-only representative demand set (EN base + FR); the
                  fixtures carry FR pipeline state. */}
              <DetailedStatusBadges
                data={f.data}
                targetLanguages={['en', 'fr']}
              />
            </Group>
          ))}
          <Text size='xs' c='dimmed'>
            Plus an <code>isProduction</code> sentinel (artworks_private doc
            with trackUrl but no language_status):
          </Text>
          <Group gap='md' wrap='nowrap'>
            <Text size='sm' w={360}>
              isProduction=true (no language_status)
            </Text>
            <DetailedStatusBadges
              data={{ localized: { fr: { trackUrl: 'https://prod/x.mp3' } } }}
              targetLanguages={['en', 'fr']}
              isProduction
            />
          </Group>
        </Stack>
      </Card>

      <Card withBorder shadow='xs' padding='md' radius='md'>
        <Stack>
          <Title order={4}>ApproveButton — 4 states</Title>
          <ApproveButtonRow />
        </Stack>
      </Card>

      <Card withBorder shadow='xs' padding='md' radius='md'>
        <Stack>
          <Title order={4}>AudioPill — 3 states</Title>
          <AudioPillRow />
        </Stack>
      </Card>

      {/* TourTrackRow skeleton card removed in M4.3 commit 1c —
          the M3.3 stub at src/components/TourTrackRow.tsx was
          deleted; the production-ready replacement lives at
          src/components/tour-planner/TrackRow.tsx and is rendered
          inside <TrackOrderingList> on the /tour-planner/$planId
          route, which exercises it against real Firestore data.
          A standalone demo here would require mocking the
          per-row useFirestoreDoc + useSortable hooks; not worth
          the complexity for a demo route. */}

      <Card withBorder shadow='xs' padding='md' radius='md'>
        <Stack>
          <Title order={4}>BulkActionMenu (skeleton)</Title>
          <BulkActionMenu />
          <Text size='xs' c='dimmed'>
            Real action set lands in M4.4 (Tour Planner) + M6.3 (Dashboards).
          </Text>
        </Stack>
      </Card>

      <Card withBorder shadow='xs' padding='md' radius='md'>
        <Stack>
          <Title order={4}>useOptimisticMutation</Title>
          <OptimisticMutationDemo />
        </Stack>
      </Card>
    </Stack>
  );
};

export const Route = createFileRoute('/_authenticated/components')({
  component: ComponentsDemoRoute,
});
